import { useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'

/**
 * PDFViewerModal - A reusable modal for displaying PDF documents
 *
 * Displays a PDF in a full-width modal with minimal chrome:
 * - Title bar with invoice info and close button
 * - Full-width PDF viewer (supports both PDFViewer and iframe)
 * - Floating download button overlaid on PDF (bottom-right)
 * - Navigation buttons for prev/next (bottom-center, optional)
 * - Keyboard shortcuts: ← → for navigation, Esc to close
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Title to display in modal header
 * @param {React.ReactNode} props.pdfContent - The PDF content to display (PDFViewer or iframe)
 * @param {Function} props.onDownload - Callback when download button is clicked
 * @param {string} props.downloadState - Download button state: 'default', 'downloading', 'success', 'error'
 * @param {Function} props.onPrevious - Optional callback for previous navigation
 * @param {Function} props.onNext - Optional callback for next navigation
 * @param {boolean} props.hasPrevious - Whether previous navigation is available
 * @param {boolean} props.hasNext - Whether next navigation is available
 */
export default function PDFViewerModal({
  isOpen,
  onClose,
  title,
  pdfContent,
  onDownload,
  downloadState = 'default',
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false
}) {
  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      // Arrow key navigation
      if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        e.preventDefault()
        onPrevious()
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        e.preventDefault()
        onNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, hasPrevious, hasNext, onPrevious, onNext])
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose()
      }
    }}>
      <DialogContent className="odin-modal-large p-0 flex flex-col h-[92vh]">
        {/* Title bar */}
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* PDF Content - Full width with floating buttons */}
        <div className="flex-1 relative overflow-hidden min-h-0 pb-6">
          <div className="absolute inset-0 px-6 pb-6">
            {pdfContent}
          </div>

          {/* Navigation Buttons - Bottom Center */}
          {(onPrevious || onNext) && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-2">
              <Button
                onClick={onPrevious}
                disabled={!hasPrevious}
                variant="outline"
                size="icon"
                className="shadow-lg bg-white hover:bg-white"
                title="Previous invoice (←)"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                onClick={onNext}
                disabled={!hasNext}
                variant="outline"
                size="icon"
                className="shadow-lg bg-white hover:bg-white"
                title="Next invoice (→)"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Floating Download Button - Bottom Right */}
          {onDownload && (
            <div className="absolute bottom-10 right-10 z-10">
              <Button
                onClick={onDownload}
                disabled={downloadState === 'downloading'}
                className="shadow-lg"
              >
                {downloadState === 'downloading' && '⬇️ Downloading...'}
                {downloadState === 'success' && '✅ Downloaded!'}
                {downloadState === 'error' && '❌ Download Failed'}
                {downloadState === 'default' && 'Download PDF'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
